import cx from 'classnames';
import Trans from 'next-translate/Trans';
import useTranslation from 'next-translate/useTranslation';
import React, { FunctionComponent, useMemo, useState } from 'react';

import { escapeRegExp } from '../../../../utils/escapeRegExp';
import Breadcrumbs from '../../../modules/breadcrumbs';
import Help from '../../../modules/help';
import TalkToUs from '../../../modules/help/talk-to-us';
import CollapsibleList from '../../../ui/legacy/collapsible-list/definition';
import TextInput from '../../../ui/inputs/text';

import FaqSchema from './seo';

const GroupFaqsPage: FunctionComponent<TPageFaqsofGroup> = ({
  faqsOfGroup,
  hash,
}) => {
  const { t } = useTranslation();
  const breadcrumbItems = [];
  breadcrumbItems.push(
    ...[
      {
        href: t('routes:faqs'),
        text: t('common:faqs'),
      },
    ]
  );

  const [search, setSearch] = useState<string>('');
  const filteredFaqs: Array<TFaqs> = useMemo(() => {
    if (search) {
      return faqsOfGroup.faqs.filter(
        (faq) =>
          faq.answer?.toLowerCase().includes(search.toLowerCase()) ||
          faq.question.toLocaleLowerCase().includes(search.toLowerCase())
      );
    }
    return faqsOfGroup.faqs;
  }, [search, faqsOfGroup.faqs]);

  const regex = new RegExp(escapeRegExp(`\(${search}\)`), 'ig');

  return (
    <>
      <FaqSchema breadcrumbs={breadcrumbItems} faqsOfGroup={faqsOfGroup} />
      <Breadcrumbs
        items={breadcrumbItems}
        currentPage={faqsOfGroup.group.label}
      />
      <div
        className={cx(
          'container',
          'flex',
          'flex-col',
          'mx-auto',
          'lg:my-12',
          'my-4'
        )}
      >
        <div className="flex justify-between lg:mb-0 lg:col-span-2">
          <div>
            <h1 className="px-4 font-bold leading-8  text-2xl">
              {faqsOfGroup.group.label}{' '}
              <small className="font-normal text-lg">
                ({t('common:faqs')})
              </small>
            </h1>
            <p className="px-4 text-sm">{t('faqs:description')}</p>
          </div>
          <aside className="hidden lg:block">
            <span className="flex justify-end">
              <TalkToUs variant="default" />
            </span>
            <p className={cx('my-2', 'text-sm', '')}>
              <Trans
                i18nKey="help:availability"
                components={{ strong: <strong /> }}
                values={{ timezone: `(GMT+${false ? '1' : '0'})` }}
              />
            </p>
          </aside>
        </div>
        <section className="lg:col-span-2 mt-4">
          <div className="my-4 lg:w-1/3 mx-auto">
            <TextInput
              value={search}
              onChange={(e) => setSearch(e.currentTarget.value)}
              label={t('faqs:filter_placeholder')}
              labelBehaviour="animated"
              variant="filter"
              border
            />
          </div>
          <CollapsibleList
            className="px-4"
            activeItems={search ? filteredFaqs.map(({ slug }) => slug) : [hash]}
            multiOpen={true}
            searchKey={search}
            items={filteredFaqs.map(({ slug, question, answer }) => ({
              key: slug,
              term: question,
              description: search ? (
                <div>
                  {answer.split(regex).map((value, index) => {
                    if (value && value.match(regex) && search) {
                      return (
                        <span
                          key={value + index}
                          className="font-bold text-blue-100"
                        >
                          {value}
                        </span>
                      );
                    }
                    return (
                      <span
                        key={value + index}
                        dangerouslySetInnerHTML={{ __html: value || '' }}
                      />
                    );
                  })}
                </div>
              ) : (
                <div dangerouslySetInnerHTML={{ __html: answer || '' }} />
              ),
            }))}
          />
          <div className="px-4 mt-8 lg:hidden">
            <Help />
          </div>
        </section>
      </div>
    </>
  );
};

export default GroupFaqsPage;
